import { type MutableRefObject, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import type { NewShowCaseList } from '@/app/types';
import { CommunityEnum, type ExploreTab, InspirationActiveType, type UpdateInteractionStatusParams } from '@/app/types';
import { viduFeedList } from '../apis';
import type { ViduFeedListData } from '../types';

const PAGE_SIZE = 20;

interface Feed {
  handleEnter: () => void;
  pageToken: MutableRefObject<string>;
  updateCreationSerialId: (num: number) => void;
  updatePageInfo: ({ token }: { token?: string }) => void;
  exploreTab: CommunityEnum;
  mixed?: boolean;
  hasMore?: boolean;
  activeType?: InspirationActiveType;
  /** Optional overrides to be merged into feed list query (e.g., scene, sort_type) */
  queryOverrides?: ViduFeedListData['query'];
}

export const useCommunityFeed = ({
  handleEnter,
  pageToken,
  updatePageInfo,
  updateCreationSerialId,
  activeType,
  exploreTab,
  mixed,
  queryOverrides,
}: Feed) => {
  const [inspAssets, setInspAssets] = useState<NewShowCaseList>([]);

  const { run, loading } = useFetch(
    (query: ViduFeedListData['query']) => {
      return viduFeedList({
        query: {
          'pager.page_token': pageToken.current,
          'pager.pagesz': PAGE_SIZE,
          ...query,
        },
      });
    },
    {
      manual: true,
      onSuccess: ({ data, error, response }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
        }
        if (data) {
          const traceId = response.headers.get('x-md-trace-id') || '';
          const newItems = (data.list || []).map((item) => ({
            ...item,
            traceId,
          }));
          const isFirstPage = !pageToken.current;
          setInspAssets((prevAssets) => (isFirstPage ? newItems : [...prevAssets, ...newItems]));
          updatePageInfo({ token: data.next_page_token || '' });
        }
      },
    },
  );

  const getData = () => {
    if (!pageToken.current) {
      setInspAssets((prev) => (prev.length === 0 ? prev : []));
    }
    run({
      ...(exploreTab === CommunityEnum.MediaAsset
        ? {
            channel_type: mixed ? 'mixed' : 'media_asset',
          }
        : {
            ...(exploreTab === CommunityEnum.ShortFilm
              ? {
                  channel_type: 'short_film',
                  series_feed: true,
                }
              : {
                  channel_type: 'tutorial',
                }),
          }),
      ...(!mixed ? { sort_type: activeType !== InspirationActiveType.Latest ? 'recommended' : 'latest' } : {}),
      ...(queryOverrides || {}),
    });
  };

  const handleDetail = (id: string, _traceId: string) => {
    handleEnter();
    const creationIndex = inspAssets.findIndex((item) =>
      [
        item.image_asset?.id,
        item.media_asset?.id,
        item.short_film?.id,
        item.tutorial?.id,
        item.mini_video?.id,
      ].includes(id),
    );
    if (creationIndex !== undefined && creationIndex !== -1) {
      updateCreationSerialId(creationIndex);
    }
  };

  /**
   * 灵感/短片/教程 收藏/点赞之后更新状态
   */
  const updateInteractionStatus = ({ id, obj_type, voted, collected }: UpdateInteractionStatusParams) => {
    const type = obj_type as ExploreTab;

    setInspAssets((prevAssets) => {
      return prevAssets.map((asset) => {
        const targetItem = asset[type];
        if (!targetItem || targetItem.id !== id || !targetItem.react_info) {
          return asset;
        }

        const nextReactInfo = { ...targetItem.react_info };

        if (typeof voted === 'boolean') {
          nextReactInfo.vote_count = (nextReactInfo.vote_count ?? 0) + (voted ? 1 : -1);
          nextReactInfo.voted = voted;
        }

        if (typeof collected === 'boolean') {
          nextReactInfo.collect_count = (nextReactInfo.collect_count ?? 0) + (collected ? 1 : -1);
          nextReactInfo.collected = collected;
        }

        return {
          ...asset,
          [type]: {
            ...targetItem,
            react_info: nextReactInfo,
          },
        };
      });
    });
  };

  const init = () => {
    setInspAssets((prev) => (prev.length === 0 ? prev : []));
  };

  return {
    getData,
    handleDetail,
    updateInteractionStatus,
    init,
    loading,
    inspAssets,
  };
};
